/**
 * Asset Form component for creating and editing gold assets
 * Using React Hook Form with Zod validation
 */

import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import * as z from 'zod'
import { useGoldPrices } from '@/services/api/goldPrice'
import { useCreateAsset, useUpdateAsset } from '@/services/api/assets'
import { formatForDateInput, localToUtc, getTodayDateInput } from '@/utils/dateUtils'
import type { CreateGoldAssetInput, UpdateGoldAssetInput, GoldAsset } from '@/types/asset'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { DatePicker } from '@/components/common/DatePicker'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Card, CardContent } from '@/components/ui/card'

const assetFormSchema = z.object({
  name: z.string().min(1, 'Tên tài sản là bắt buộc'),
  goldTypeId: z.number().min(1, 'Vui lòng chọn loại vàng'),
  goldTypeName: z.string().optional(),
  quantity: z.number().min(0.1, 'Số lượng phải lớn hơn 0').max(1000, 'Số lượng không được vượt quá 1000'),
  purchasePrice: z.number().min(1, 'Giá mua phải lớn hơn 0'),
  purchaseDate: z.string().min(1, 'Ngày mua là bắt buộc'),
  notes: z.string().optional(),
})

type AssetFormValues = z.infer<typeof assetFormSchema>

interface AssetFormProps {
  asset?: GoldAsset
  onSuccess?: () => void
  onCancel?: () => void
  firstInputRef?: React.RefObject<HTMLInputElement>
}

export function AssetForm({ asset, onSuccess, onCancel, firstInputRef }: AssetFormProps) {
  const isEdit = !!asset
  const { 
    data: goldTypes, 
    isLoading: loadingTypes,
    error: goldTypesError,
    refetch: refetchGoldTypes
  } = useGoldPrices()
  const createMutation = useCreateAsset()
  const updateMutation = useUpdateAsset()

  const form = useForm<AssetFormValues>({
    resolver: zodResolver(assetFormSchema),
    defaultValues: {
      name: asset?.name || '',
      goldTypeId: asset?.goldTypeId || 0,
      goldTypeName: asset?.goldTypeName || '',
      quantity: asset?.quantity || 0,
      purchasePrice: asset?.purchasePrice || 0,
      purchaseDate: asset ? formatForDateInput(asset.purchaseDate) : getTodayDateInput(),
      notes: asset?.notes || '',
    },
  })

  // Sync form when asset changes
  useEffect(() => {
    if (asset) {
      form.reset({
        name: asset.name || '',
        goldTypeId: asset.goldTypeId || 0,
        goldTypeName: asset.goldTypeName || '',
        quantity: asset.quantity || 0,
        purchasePrice: asset.purchasePrice || 0,
        purchaseDate: formatForDateInput(asset.purchaseDate),
        notes: asset.notes || '',
      })
    }
  }, [asset, form])

  // Sync gold type name when goldTypeId changes
  useEffect(() => {
    const goldTypeId = form.watch('goldTypeId')
    if (goldTypes && goldTypeId && goldTypeId !== 0) {
      const type = goldTypes.find((t) => t.Id === goldTypeId)
      if (type) {
        form.setValue('goldTypeName', type.TypeName, { shouldValidate: false })
      }
    }
  }, [form.watch('goldTypeId'), goldTypes, form])

  const onSubmit = async (data: AssetFormValues) => {
    try {
      const purchaseDateUtc = localToUtc(new Date(data.purchaseDate))

      if (isEdit && asset) {
        const updateInput: UpdateGoldAssetInput = {
          name: data.name,
          goldTypeId: data.goldTypeId,
          goldTypeName: data.goldTypeName || '',
          quantity: data.quantity,
          purchasePrice: data.purchasePrice,
          purchaseDate: purchaseDateUtc,
          ...(data.notes ? { notes: data.notes } : {}),
        }
        await updateMutation.mutateAsync({ id: asset.id, input: updateInput })
      } else {
        const createInput: CreateGoldAssetInput = {
          name: data.name,
          goldTypeId: data.goldTypeId,
          goldTypeName: data.goldTypeName || '',
          quantity: data.quantity,
          purchasePrice: data.purchasePrice,
          purchaseDate: purchaseDateUtc,
          ...(data.notes ? { notes: data.notes } : {}),
        }
        await createMutation.mutateAsync(createInput)
      }

      onSuccess?.()
      toast.success(isEdit ? 'Đã cập nhật tài sản thành công' : 'Đã tạo tài sản thành công')
    } catch (error) {
      console.error('Failed to save asset:', error)
      let errorMessage = 'Không thể lưu tài sản. Vui lòng thử lại.'
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
          errorMessage = 'Lỗi kết nối đến server. Vui lòng kiểm tra kết nối mạng hoặc cấu hình CORS.'
        } else if (error.message.includes('500')) {
          errorMessage = 'Lỗi server. Vui lòng thử lại sau hoặc liên hệ quản trị viên.'
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Bạn không có quyền thực hiện thao tác này. Vui lòng đăng nhập lại.'
        } else if (error.message.includes('404')) {
          errorMessage = 'Không tìm thấy tài sản. Vui lòng làm mới trang và thử lại.'
        } else {
          errorMessage = error.message || errorMessage
        }
      }
      
      form.setError('root', { message: errorMessage })
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {form.formState.errors.root && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
          </CardContent>
        </Card>
      )}

      <FieldGroup>
        <Controller
          name="name"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="asset-form-name">
                Tên tài sản <span className="text-destructive">*</span>
              </FieldLabel>
              <Input
                {...field}
                id="asset-form-name"
                ref={firstInputRef}
                aria-invalid={fieldState.invalid}
                placeholder="Ví dụ: Vàng SJC 9999"
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="goldTypeId"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="asset-form-goldTypeId">
                Loại vàng <span className="text-destructive">*</span>
              </FieldLabel>
              {goldTypesError ? (
                <div className="space-y-2">
                  <p className="text-red-500 text-sm" role="alert" aria-live="polite">
                    Không thể tải danh sách loại vàng. Vui lòng thử lại.
                  </p>
                  <Button 
                    type="button" 
                    onClick={() => refetchGoldTypes()}
                    variant="outline"
                    size="sm"
                    className="min-h-[44px]"
                    aria-label="Thử lại tải danh sách loại vàng"
                  >
                    Thử lại
                  </Button>
                </div>
              ) : loadingTypes ? (
                <div className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center" aria-busy="true">
                  Đang tải...
                </div>
              ) : !goldTypes || goldTypes.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Không có loại vàng nào khả dụng.
                </p>
              ) : (
                <Select
                  value={field.value?.toString() || '0'}
                  onValueChange={(value) => {
                    const numValue = Number(value)
                    field.onChange(numValue)
                    if (numValue !== 0) {
                      const type = goldTypes.find((t) => t.Id === numValue)
                      if (type) {
                        form.setValue('goldTypeName', type.TypeName, { shouldValidate: false })
                      }
                    }
                  }}
                >
                  <SelectTrigger id="asset-form-goldTypeId" aria-invalid={fieldState.invalid}>
                    <SelectValue placeholder="Chọn loại vàng" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Chọn loại vàng</SelectItem>
                    {goldTypes.map((type) => (
                      <SelectItem key={type.Id} value={type.Id.toString()}>
                        {type.TypeName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="quantity"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="asset-form-quantity">
                Số lượng (lượng) <span className="text-destructive">*</span>
              </FieldLabel>
              <Input
                {...field}
                id="asset-form-quantity"
                type="number"
                step="0.1"
                min="0"
                max="1000"
                value={field.value || ''}
                onChange={(e) => {
                  const value = e.target.value
                  const numValue = value === '' ? 0 : parseFloat(value)
                  field.onChange(isNaN(numValue) ? 0 : numValue)
                }}
                onFocus={(e) => {
                  if (field.value === 0) {
                    setTimeout(() => e.target.select(), 0)
                  }
                }}
                aria-invalid={fieldState.invalid}
                placeholder="0"
              />
              <FieldDescription>
                Nhập số lượng vàng (đơn vị: lượng)
              </FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="purchasePrice"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="asset-form-purchasePrice">
                Giá mua (VND) <span className="text-destructive">*</span>
              </FieldLabel>
              <CurrencyInput
                id="asset-form-purchasePrice"
                name={field.name}
                value={field.value?.toString() || ''}
                onValueChange={(value) => {
                  const numValue = value ? parseFloat(value) : undefined
                  field.onChange(numValue)
                }}
                onBlur={field.onBlur}
                aria-invalid={fieldState.invalid}
                placeholder="Ví dụ: 75,000,000"
              />
              <FieldDescription>
                Nhập giá mua mỗi lượng vàng (đơn vị: VND). Ví dụ: 75,000,000
              </FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="purchaseDate"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="asset-form-purchaseDate">
                Ngày mua <span className="text-destructive">*</span>
              </FieldLabel>
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                id="asset-form-purchaseDate"
                aria-invalid={fieldState.invalid}
                placeholder="Chọn ngày mua"
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="notes"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="asset-form-notes">
                Ghi chú
              </FieldLabel>
              <Textarea
                {...field}
                id="asset-form-notes"
                rows={3}
                aria-invalid={fieldState.invalid}
                placeholder="Thêm ghi chú về tài sản này (tùy chọn)"
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>

      <div className="space-y-3">
        {(createMutation.isPending || updateMutation.isPending) && (
          <Progress value={100} className="w-full" />
        )}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" onClick={onCancel} variant="outline" className="min-h-[44px]" disabled={createMutation.isPending || updateMutation.isPending}>
              Hủy
            </Button>
          )}
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="min-h-[44px]"
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Đang lưu...'
              : isEdit
                ? 'Cập nhật'
                : 'Tạo mới'}
          </Button>
        </div>
      </div>
    </form>
  )
}
